import React, { useState, useEffect, useMemo } from 'react';
// import { useParams } from 'react-router-dom'; // Not using useParams if cohortId is a prop
import {
    Box,
    Typography,
    CircularProgress,
    Alert,
    Paper,
    Grid,
    Divider,
    Card,
    CardContent,
    Chip,
    Container,
    List,
    ListItem,
    // ListItemIcon, // Not directly used, icons are in Typography
    // ListItemText, // Not directly used, Typography is used
    Accordion,
    AccordionSummary,
    AccordionDetails
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
// import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'; // Not used
import EmojiPeopleRoundedIcon from '@mui/icons-material/EmojiPeopleRounded'; // Changed for Confidence
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'; // For Warmth

import { 
    ResponsiveContainer, 
    ScatterChart, 
    Scatter, 
    XAxis, 
    YAxis, 
    CartesianGrid, 
    Tooltip,
    Label,
    ReferenceArea
} from 'recharts';
import api from './api';
import config from './config';

const getCurrentUserId = () => {
    return localStorage.getItem('userId');
};

// Helper function to determine Charisma Zone Name
const getCharismaZoneName = (warmth, confidence) => {
    if (warmth === null || warmth === undefined || confidence === null || confidence === undefined) return "N/A";
    const wq = parseFloat(warmth);
    const cq = parseFloat(confidence);
    if (wq > 0 && cq > 0) return "Charismatic Zone";
    if (wq > 0 && cq <= 0) return "Warmth Zone";
    if (wq <= 0 && cq > 0) return "Confidence Zone";
    if (wq <= 0 && cq <= 0) return "Danger Zone!";
    return "Not Determined";
};

const zoneSpecificContent = {
    "Danger Zone!": {
        title: "Understanding the Danger Zone",
        description: "Landing in the Danger Zone indicates that you have significant room to improve your perceived warmth and confidence. There are many warmth and confidence cues you can choose from to develop over the next few weeks, so I encourage you to select and prioritize those that will be most manageable for you to work on.",
        struggles: "People who do not send enough of either warmth or confidence cues are more likely to be overlooked, dismissed, pitied, and undervalued. It is possible that people who have landed in the Danger Zone may possess warmth and/or confidence, but they might be masking or hiding their true feelings and are not sending sufficient cues."
    },
    "Warmth Zone": {
        title: "Understanding the Warmth Zone",
        description: "Landing in the Warmth Zone indicates that you are perceived as someone who is kind, approachable, and trustworthy. This also means that you have plenty of room for improvement in perceptions of your confidence; after all, warmth without confidence can lead others to also perceive you as weak, ineffective, or easy to take advantage of. As such, you should plan to focus on developing your confidence cues.",
        strengths: "Warm communicators quickly build rapport, are easy to collaborate with, and make people feel comfortable.",
        struggles: "High-warmth communicators may struggle to say no or to set appropriate boundaries for fear of disappointing others or being disliked. For some high-warmth communicators, this may be a means to compensate for your own feelings of inadequacy or imposter syndrome."
    },
    "Confidence Zone": {
        title: "Understanding the Confidence Zone",
        description: "Landing in the Confidence Zone indicates that you are perceived as someone who is skilled, persuasive, and effective. This also means that you have plenty of room for improvement in perceptions of your warmth; after all, confidence without warmth can lead others to perceive you as bombastic, manipulative, or unpleasant to work with, even if you do complete your assigned tasks well. As such, you should plan to focus on developing your warmth cues.",
        strengths: "Confident communicators are perceived as efficient, productive, and capable of directing and leading others.",
        struggles: "High-confidence communicators might have difficulty building rapport, be considered hard to talk to, or be seen as cold or intimidating. This can make them less likely to earn buy-in to their ideas or win collaborators to their causes."
    },
    "Charismatic Zone": {
        title: "Understanding the Charismatic Zone",
        description: "Landing in the Charismatic Zone indicates that you are perceived as someone who possesses both warmth and confidence. Colleagues likely find you approachable, trustworthy, skilled, and effective. While starting off in the Charismatic Zone is a great start, either side of the equation may prove more challenging for you to maintain; think about how you can continue developing your warmth and confidence cues to both maintain and further strengthen your charisma.",
        strengths: "Highly charismatic people are both effective and approachable. They are great team leaders and communicators.",
        struggles: "Highly charismatic people are often sought after and in-demand. They might struggle to balance all the opportunities they have. Some people who are perceived as highly charismatic may require significant cognitive or emotional effort to maintain their highly charismatic nature and could risk burnout."
    }
};

const confidenceTips = [
    { title: "Power-Pose", text: "Stand up straight and tall with your weight evenly distributed over both feet. Imagine that there is a string that runs from the base of your sacrum up through your spine and is pulling you up through the top of your head. Take up as much vertical space as you can while keeping both feet firmly planted on the ground." },
    { title: "Flex Your Lower Lid", text: "Raise your lower eyelids slightly to signal deeper concentration. Making your eyes smaller in this way signals less fear (our eyes go big and wide when we are scared) and allows us to take in greater fine detail by blocking out excess light. This is the classic \"Blue Steel\" look." },
    { title: "Move With Intention", text: "Signal to your audience that you know what you're talking about by only moving with intention. Intentional and fluid motion signals confidence far more than jerky and seemingly random movements." },
    { title: "Gesture to Explain", text: "Use your hands to help you emphasize key points you make while you speak; purposeful, confident gestures can convey 400 percent more information than spoken content, increasing audience comprehension by up to 60%!" },
    { title: "Speak in a Low Register", text: "Consistently use the lowest natural pitch of your voice to signal confidence in what you have to say. Do not raise the inflection at the end of a sentence (also known as \"uptalk\"), which sounds like you are questioning yourself." },
    { title: "Take Pauses", text: "Remind yourself to pause in between words and sentences where appropriate. Imagine what you're saying is written down, and use the places where you would place punctuation as indicators for when pauses would sound natural. Pauses can also help you capture your audience's attention by adding intrigue and suspense." },
    { title: "Use Data", text: "Cite experts, data, and statistics to demonstrate that your claims are supported by credible evidence." },
    { title: "Use Task-Focused Language", text: "When talking with others, emphasize your own competence and the value you bring to work projects." }
];

const warmthTips = [
    { title: "Use Your Head", text: "The position and movement of your head while engaging with others can signal interest, attention,and respect. Tilt your head slightly, which indicates a desire to hear others better and displaying more vulnerability by exposing more of your neck. Nod your head to signify that you aren't just listening to respond but are listenting to understand." },
    { title: "Smile With Your Whole Face", text: "Not only does an authentic smile make you seem more approachable, it also makes you more memorable. Smiling with your full face means not just lifting the corners of your mouth in a Joker-style rictus but allowing the smile to affect the shape of your full face, pushing your cheeks up into your eye zone and crinkling the areas around your eyes. People can tell an authentic from a fake smile and will always respond accordingly!" },
    { title: "Vary Your Vocal Delivery", text: "Find the emotional connection with what you're saying and allow that emotion to infuse what you say. If you sound bored, your audience will be bored. If you want your audience to feel excited, angry, energized, curious, or any other emotion, you need to be the role model for them in how speak. Not only will this make you sound more interesting, but it will also actually make you sound even more confident!" },
    { title: "Tell Stories", text: "Telling stories about people makes your topics more relatable, more human than rattling off a bunch of facts and statistics. Telling someone's story - whether your own, someone else's, or a hypothetical subject - signals to your audience that you understand the human impact of your content." },
    { title: "Use Relationship-Focused Language", text: "When talking with others, incorporate words and expressions that make them feel like they are welcome, that they are valued, and that they belong. Express your appreciation for your relationship and the bonds you have with each other." }
];


const CustomCharismaPoint = (props) => {
    const { cx, cy, payload } = props; 
    const radius = 15; 
    const strokeColor = "#1a237e"; // Dark indigo
    const fillColor = "#7986cb"; // Lighter indigo
    const strokeWidth = 2;
    if (cx == null || cy == null || payload.x === null || payload.y === null) return null;
    return (<circle cx={cx} cy={cy} r={radius} fill={fillColor} stroke={strokeColor} strokeWidth={strokeWidth}/>);
};


function CharismaDiagnosticResults({ cohortId }) {
    const [results, setResults] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const userId = useMemo(() => getCurrentUserId(), []);

    useEffect(() => {
        if (!userId || cohortId === null || cohortId === undefined) {
            setError("User ID or Cohort ID is missing. Cannot fetch results.");
            setLoading(false);
            return;
        }
        const fetchResults = async () => {
            setLoading(true); setError(null);
            try {
                const response = await api.get(`${config.backendUrl}/api/charisma/results/${userId}/${cohortId}`);
                setResults(response.data);
            } catch (err) {
                setError(err.response?.data?.message || err.message || "Failed to load Charisma Diagnostic results.");
                setResults(null); // Ensure results are null on error
            } finally {
                setLoading(false);
            }
        };
        fetchResults();
    }, [userId, cohortId]);

    const charismaZone = useMemo(() => {
        if (!results) return "N/A";
        return getCharismaZoneName(results.warmth_quotient, results.confidence_quotient);
    }, [results]);

    const currentZoneContent = useMemo(() => {
        return zoneSpecificContent[charismaZone] || null;
    }, [charismaZone]);


    const renderCharismaGraph = () => {
        if (!results || results.confidence_quotient === null || results.warmth_quotient === null) {
            return <Box sx={{textAlign: 'center', my:2}}><Typography color="textSecondary">Graph data not available.</Typography></Box>;
        }
        const plotData = [{ 
            x: parseFloat(results.confidence_quotient), 
            y: parseFloat(results.warmth_quotient), 
            name: "Your Charisma Profile" 
        }];
        const axisTicks = Array.from({ length: 21 }, (_, i) => i - 10); // -10 to 10
        const quadrantLabelFontSize = 16; // Increased font size

        return (
            <Card sx={{ mb: 3, p: {xs: 1, sm: 2} }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom align="center">Your Charisma Profile</Typography>
                     <Typography variant="body2" sx={{mb: 2, textAlign: 'center'}}>
                        This graph plots your Confidence Quotient (horizontal axis) and Warmth Quotient (vertical axis).
                        Understanding your position can help you identify areas for development.
                    </Typography>
                    <ResponsiveContainer width="90%" aspect={1}> 
                        <ScatterChart margin={{ top: 20, right: 40, bottom: 40, left: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis 
                                type="number" 
                                dataKey="x" 
                                name="Confidence Quotient" 
                                domain={[-10, 10]} 
                                ticks={axisTicks} 
                                allowDataOverflow={true}
                            >
                                <Label value="Confidence Quotient" offset={-25} position="insideBottom" />
                            </XAxis>
                            <YAxis 
                                type="number" 
                                dataKey="y" 
                                name="Warmth Quotient" 
                                domain={[-10, 10]} 
                                ticks={axisTicks} 
                                allowDataOverflow={true}
                            >
                                <Label value="Warmth Quotient" angle={-90} offset={0} position="insideLeft" style={{ textAnchor: 'middle' }} />
                            </YAxis>
                            <Tooltip cursor={{ strokeDasharray: '3 3' }} />
                            
                            {/* Quadrant Definitions */}
                            <ReferenceArea x1={0.01} x2={10} y1={0.01} y2={10} stroke="none" fill="#a5d6a7" fillOpacity={0.3} label={{ value: "Charismatic Zone", position: "insideTopRight", fill:"#2e7d32", fontSize: quadrantLabelFontSize }}/>
                            <ReferenceArea x1={-10} x2={-0.01} y1={0.01} y2={10} stroke="none" fill="#fff59d" fillOpacity={0.3} label={{ value: "Warmth Zone", position: "insideTopLeft", fill:"#f9a825", fontSize: quadrantLabelFontSize }}/>
                            <ReferenceArea x1={0.01} x2={10} y1={-10} y2={-0.01} stroke="none" fill="#90caf9" fillOpacity={0.3} label={{ value: "Confidence Zone", position: "insideBottomRight", fill:"#1565c0", fontSize: quadrantLabelFontSize }}/>
                            <ReferenceArea x1={-10} x2={-0.01} y1={-10} y2={-0.01} stroke="none" fill="#ef9a9a" fillOpacity={0.3} label={{ value: "Danger Zone!", position: "insideBottomLeft", fill:"#c62828", fontSize: quadrantLabelFontSize }}/>
                            
                            {/* Center Lines */}
                            <ReferenceArea x1={-0.1} x2={0.1} fill="gray" fillOpacity={0.2} yAxisId={0} ifOverflow="visible" />
                            <ReferenceArea y1={-0.1} y2={0.1} fill="gray" fillOpacity={0.2} xAxisId={0} ifOverflow="visible" />
                            
                            <Scatter name="Your Profile" data={plotData} shape={<CustomCharismaPoint />} />
                        </ScatterChart>
                    </ResponsiveContainer>
                </CardContent>
            </Card>
        );
    };

    if (loading) {
        return <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress /></Box>;
    }
    if (error) {
        return <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>;
    }
    if (!results || !results.user_info) { 
        return <Alert severity="info" sx={{ m: 2 }}>No Charisma Diagnostic results data found or user information is missing.</Alert>;
    }

    return (
        <Container maxWidth="lg" sx={{ py: 3 }}>
            <Paper elevation={3} sx={{ p: { xs: 2, sm: 3 } }}>
                <Typography variant="h4" component="h1" gutterBottom align="center">
                    Charisma Diagnostic Results
                </Typography>
                <Typography variant="subtitle1" align="center" gutterBottom>
                    For: {results.user_info.name || `User ID: ${results.user_info.user_id}`} 
                    {results.cohort_info && ` (Cohort: ${results.cohort_info.cohort_name || `ID ${results.cohort_info.cohort_id}`})`}
                </Typography>
                 <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
                    Completed on: {results.completed_at ? new Date(results.completed_at).toLocaleDateString() : 'N/A'}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Grid container spacing={3} sx={{mb: 2}}>
                    <Grid item xs={12} md={4}>
                        <Card raised sx={{height: '100%'}}>
                            <CardContent sx={{textAlign: 'center'}}>
                                <Typography variant="h6">Warmth Quotient</Typography>
                                <Typography variant="h3" color="primary.main" sx={{fontWeight: 'bold', my:1}}>
                                    {results.warmth_quotient !== null ? results.warmth_quotient.toFixed(2) : 'N/A'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">Score range: -10 to 10</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card raised sx={{height: '100%'}}>
                            <CardContent sx={{textAlign: 'center'}}>
                                <Typography variant="h6">Confidence Quotient</Typography>
                                <Typography variant="h3" color="secondary.main" sx={{fontWeight: 'bold', my:1}}>
                                    {results.confidence_quotient !== null ? results.confidence_quotient.toFixed(2) : 'N/A'}
                                </Typography>
                                 <Typography variant="caption" color="text.secondary">Score range: -10 to 10</Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Card raised sx={{height: '100%'}}>
                            <CardContent sx={{textAlign: 'center'}}>
                                <Typography variant="h6">Your Charisma Zone</Typography>
                                <Chip 
                                    label={charismaZone} 
                                    color={
                                        charismaZone === "Charismatic Zone" ? "success" :
                                        charismaZone === "Danger Zone!" ? "error" :
                                        "info" // Default for Warmth/Confidence zones
                                    }
                                    sx={{fontSize: '1.5rem', fontWeight: 'bold', my: 1.5, py: 2.5, px: 1}}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
                
                {currentZoneContent && (
                    <Card variant="outlined" sx={{ mb: 3, p: 2, backgroundColor: 'grey.50' }}>
                        <CardContent>
                            <Typography variant="h5" gutterBottom sx={{textAlign: 'left'}}>{currentZoneContent.title}</Typography>
                            <Typography variant="body1" paragraph sx={{textAlign: 'left', whiteSpace: 'pre-line'}}>
                                {currentZoneContent.description}
                            </Typography>
                            {currentZoneContent.strengths && (
                                <>
                                  <Typography variant="subtitle1" sx={{fontWeight: 'bold', mt:1.5, textAlign: 'left'}}>Strengths:</Typography>
                                  <Typography variant="body1" paragraph sx={{textAlign: 'left', whiteSpace: 'pre-line'}}>{currentZoneContent.strengths}</Typography>
                                </>
                            )}
                            {currentZoneContent.struggles && (
                                <>
                                  <Typography variant="subtitle1" sx={{fontWeight: 'bold', mt:1.5, textAlign: 'left'}}>Potential Struggles:</Typography>
                                  <Typography variant="body1" paragraph sx={{textAlign: 'left', whiteSpace: 'pre-line'}}>{currentZoneContent.struggles}</Typography>
                                </>
                            )}
                        </CardContent>
                    </Card>
                )}

                {renderCharismaGraph()}
                
                <Accordion sx={{mb:2}}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <EmojiPeopleRoundedIcon sx={{ mr: 1, color: 'secondary.main' }} /> Tips for Boosting Confidence Cues
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List dense>
                            {confidenceTips.map((tip, index) => (
                                <ListItem key={`conf-${index}`} alignItems="flex-start" sx={{pb:1, flexDirection: 'column', borderBottom: index < confidenceTips.length -1 ? '1px dashed #eee' : 'none'}}>
                                     <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>{tip.title}</Typography>
                                     <Typography variant="body2" color="text.secondary">{tip.text}</Typography>
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>

                <Accordion>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                         <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
                            <FavoriteBorderIcon sx={{ mr: 1, color: 'primary.main' }} /> Tips for Boosting Warmth Cues
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <List dense>
                            {warmthTips.map((tip, index) => (
                                 <ListItem key={`warm-${index}`} alignItems="flex-start" sx={{pb:1, flexDirection: 'column', borderBottom: index < warmthTips.length -1 ? '1px dashed #eee' : 'none'}}>
                                     <Typography variant="subtitle1" sx={{fontWeight: 'bold'}}>{tip.title}</Typography>
                                     <Typography variant="body2" color="text.secondary">{tip.text}</Typography>
                                </ListItem>
                            ))}
                        </List>
                    </AccordionDetails>
                </Accordion>

            </Paper>
        </Container>
    );
}

export default CharismaDiagnosticResults;
